import { applyHeroImagePositioning } from './ImagePositioning';
import { formatCurrency } from './CurrencyUtils';
import { formatPackageDuration } from './packageDuration';
import { fitShareImageUrl, getConfiguredShareImage, getPublicSiteUrl } from './publicSiteUrl';
import sitePaths from './sitePaths';

// Social Media Sharing URLs
export const SHARING_URLS = {
  whatsapp: (url: string, text: string) =>
    `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`,
  
  twitter: (url: string, text: string) => 
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(url + '\n\n' + text)}`,
  
  facebook: (url: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  
  pinterest: (url: string, image: string, description: string) => 
    `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(image)}&description=${encodeURIComponent(description)}`,
  
  linkedin: (url: string, title: string, summary: string) => 
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(summary)}`,
  
  telegram: (url: string, text: string) => 
    `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  
  email: (url: string, subject: string, body: string) =>
    `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${body}\n${url}`)}`,
  
  reddit: (url: string, title: string) => 
    `https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  
  tumblr: (url: string, title: string, caption: string) => 
    `https://www.tumblr.com/widgets/share/tool?posttype=link&title=${encodeURIComponent(title)}&caption=${encodeURIComponent(caption)}&content=${encodeURIComponent(url)}`
};

// Platform configurations
export const PLATFORMS = [
  { id: 'whatsapp', name: 'WhatsApp', icon: 'fab fa-whatsapp', color: '#25D366' },
  { id: 'twitter', name: 'X', icon: 'fab fa-twitter', color: '#1DA1F2' },
  { id: 'facebook', name: 'Facebook', icon: 'fab fa-facebook-f', color: '#1877F2' },
  { id: 'instagram', name: 'Instagram', icon: 'fab fa-instagram', color: '#E4405F' },
  { id: 'pinterest', name: 'Pinterest', icon: 'fab fa-pinterest-p', color: '#E60023' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'fab fa-linkedin-in', color: '#0077B5' },
  { id: 'telegram', name: 'Telegram', icon: 'fab fa-telegram-plane', color: '#0088CC' },
  { id: 'email', name: 'Email', icon: 'fas fa-envelope', color: '#6B7280' },
  { id: 'reddit', name: 'Reddit', icon: 'fab fa-reddit-alien', color: '#FF4500' },
  { id: 'tumblr', name: 'Tumblr', icon: 'fab fa-tumblr', color: '#001935' }
] as const;

export type SocialPlatform = typeof PLATFORMS[number]['id'];

const INSTAGRAM_HOME = 'https://www.instagram.com/';

// Current share endpoints. A visitor shares from their own account.
// WhatsApp carries the title, then the link.
// Facebook uses the public share dialog, so the post goes out from whoever is signed in.
// LinkedIn's share-offsite URL takes the link plus title and summary.
// Instagram has no web share URL, so the link is copied for the visitor to paste.
export function studioShareLinks(content: { title?: string; url: string }) {
  const title = content.title || '';
  const url = content.url || '';
  return {
    whatsapp: SHARING_URLS.whatsapp(url, title),
    instagram: INSTAGRAM_HOME,
    facebook: SHARING_URLS.facebook(url),
    email: SHARING_URLS.email(url, title, title),
    linkedin: SHARING_URLS.linkedin(url, title, title),
    copy: url,
  };
}

// Copy to clipboard utility
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand('copy');
      document.body.removeChild(textArea);
      return result;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

// Generate shareable content for different types
export interface ShareableContent {
  title: string;
  description: string;
  image: string;
  url: string;
  hashtags?: string[];
  category?: string; // optional; used for richer share text
}

export const generateShareContent = (type: 'gallery' | 'blog' | 'image' | 'package', data: any, options?: {
  displayPrice?: number | null;
  displayCurrency?: string;
  originalPrice?: number | null;
}): ShareableContent => {
  // Share links always use the public site address saved in admin. Never the browser host.
  const baseUrl = getPublicSiteUrl();
  
  // Reduced logging - only log in development or for errors
  if (process.env.NODE_ENV === 'development' && type === 'gallery') {
    console.log('🔍 generateShareContent called with:', {
      type,
      galleryName: data.name,
      hasHeroImage: !!data.heroImage
    });
  }
  
  switch (type) {
    case 'gallery':
      // Use the designated hero image if available, otherwise fall back to first image
      let heroImageUrl = '';
      let heroImage: any = null;
      
      // First, try to use the designated hero image
      if (data.heroImage && typeof data.heroImage === 'object') {
        heroImage = data.heroImage;
        heroImageUrl = heroImage.url || heroImage.secure_url || '';
        console.log('🎯 Using designated hero image for sharing:', heroImageUrl);
      } 
      // If no designated hero image, try using heroImageIndex
      else if (data.heroImageIndex !== undefined && data.heroImageIndex >= 0 && data.images && data.images[data.heroImageIndex]) {
        heroImage = data.images[data.heroImageIndex];
        if (heroImage && typeof heroImage === 'object') {
          heroImageUrl = heroImage.url || heroImage.secure_url || '';
          console.log('🎯 Using hero image at index', data.heroImageIndex, 'for sharing:', heroImageUrl);
        }
      }
      // Fall back to first image if no hero image is designated
      else if (data.images && data.images.length > 0) {
        heroImage = data.images[0];
        if (heroImage && typeof heroImage === 'object') {
          heroImageUrl = heroImage.url || heroImage.secure_url || '';
          console.log('🎯 Using first image as fallback hero for sharing:', heroImageUrl);
        }
      }
      
      // Ensure HTTPS URL for better compatibility
      if (heroImageUrl && heroImageUrl.startsWith('http://')) {
        heroImageUrl = heroImageUrl.replace('http://', 'https://');
      }
      
      // Apply hero image positioning and crop transformations
      if (heroImageUrl && heroImageUrl.includes('cloudinary.com') && heroImage && heroImage.publicId) {
        console.log('🎯 BEFORE applying transformations:', {
          originalUrl: heroImageUrl,
          position: data.heroImagePosition,
          zoom: data.heroImageZoom,
          cropBox: data.heroImageCropBox,
          heroImagePublicId: heroImage.publicId
        });
        
        // Apply positioning/crop transformations (1200x630 for Open Graph)
        const transformedUrl = applyHeroImagePositioning(
          heroImageUrl,
          data.heroImagePosition || 'center',
          1200,
          630,
          data.heroImageZoom || 1.0,
          data.heroImageCustomX,
          data.heroImageCustomY,
          data.heroImageCropBox
        );
        
        console.log('🎯 AFTER applying transformations:', {
          originalUrl: heroImageUrl,
          transformedUrl: transformedUrl,
          urlChanged: heroImageUrl !== transformedUrl
        });
        
        heroImageUrl = transformedUrl;
      } else {
        console.log('⚠️ Skipping transformations because:', {
          hasUrl: !!heroImageUrl,
          isCloudinary: heroImageUrl?.includes('cloudinary.com'),
          hasHeroImage: !!heroImage,
          hasPublicId: !!heroImage?.publicId
        });
        
        // If not a Cloudinary URL or no positioning, add basic social sharing optimization
        if (heroImageUrl && heroImageUrl.includes('cloudinary.com')) {
          // Add social sharing transformation if not already optimized
          if (!heroImageUrl.includes('w_1200') && !heroImageUrl.includes('c_fill')) {
            const baseUrl = heroImageUrl.split('/upload/')[0];
            const publicId = heroImageUrl.split('/upload/')[1];
            heroImageUrl = `${baseUrl}/upload/w_1200,h_630,c_fill,q_auto,f_auto/${publicId}`;
          }
        }
      }
      
      // Fallback to the share image saved in admin when this gallery has no hero
      if (!heroImageUrl) {
        heroImageUrl = getConfiguredShareImage();
        console.warn('⚠️ No hero image found, using the admin share image');
      }
      
      console.log('🎯 Gallery sharing - Hero image URL:', heroImageUrl);
      console.log('🎯 Gallery data:', { 
        name: data.name, 
        hasHeroImage: !!data.heroImage,
        heroImageIndex: data.heroImageIndex,
        firstImage: data.images?.[0],
        imagesCount: data.images?.length 
      });
      
      // Enhanced debugging for image URL
      if (!heroImageUrl || heroImageUrl.includes('logo512.png')) {
        console.warn('⚠️ Using fallback logo. Available data:', {
          imagesCount: data.images?.length || 0,
          firstImage: data.images?.[0] ? Object.keys(data.images[0]) : 'N/A',
          hasFirstImage: !!data.images?.[0]
        });
      } else {
        console.log('✅ Hero image URL successfully extracted from first image:', heroImageUrl);
      }
      
      console.log('🎯 Final sharing content:', {
        title: `${data.name} - Carmen Lorraine Photography`,
        description: data.description || `Check out this beautiful ${data.category} gallery by Carmen Lorraine Photography`,
        image: heroImageUrl,
        url: `${baseUrl}${sitePaths.galleryPath(data)}`
      });
      
      // Add cache-busting to the share URL to force WhatsApp to refetch when gallery updates
      const urlCacheBuster = data.updatedAt ? new Date(data.updatedAt).getTime() : Date.now();
      const shareUrl = `${baseUrl}${sitePaths.galleryPath(data)}?v=${urlCacheBuster}`;
      
      console.log('========================================');
      console.log('🔗 SHARE CONTENT GENERATED:');
      console.log('Gallery:', data.name);
      console.log('Gallery ID:', data.id);
      console.log('Updated At:', data.updatedAt);
      console.log('Cache Buster:', urlCacheBuster);
      console.log('Share URL:', shareUrl);
      console.log('Hero Position:', data.heroImagePosition);
      console.log('Crop Box:', data.heroImageCropBox);
      console.log('Final Image URL:', heroImageUrl);
      console.log('========================================');
      
      return {
        title: `${data.name} - Carmen Lorraine Photography`,
        description: data.excerpt || data.description || `Check out this beautiful ${data.category} gallery by Carmen Lorraine Photography`,
        image: heroImageUrl,
        url: shareUrl,
        hashtags: ['photography', data.category?.toLowerCase(), 'carmenlorraine', 'art'],
        category: data.category
      };
    
    case 'blog':
      // Get the featured image - handle both string and object formats
      let blogImageUrl = '';
      if (data.featuredImage) {
        if (typeof data.featuredImage === 'object') {
          blogImageUrl = data.featuredImage.url || data.featuredImage.secure_url || '';
        } else if (typeof data.featuredImage === 'string') {
          blogImageUrl = data.featuredImage;
        }
      }
      
      // Ensure HTTPS URL for better compatibility
      if (blogImageUrl && blogImageUrl.startsWith('http://')) {
        blogImageUrl = blogImageUrl.replace('http://', 'https://');
      }
      
      // Optimize blog image for social sharing (1200x630 for Open Graph)
      if (blogImageUrl && blogImageUrl.includes('cloudinary.com')) {
        // Add social sharing transformation if not already optimized
        if (!blogImageUrl.includes('w_1200') && !blogImageUrl.includes('c_fill')) {
          const baseUrl = blogImageUrl.split('/upload/')[0];
          const publicId = blogImageUrl.split('/upload/')[1];
          blogImageUrl = `${baseUrl}/upload/w_1200,h_630,c_fill,q_auto,f_auto/${publicId}`;
        }
      }
      
      // Fallback to the share image saved in admin when this post has no image
      if (!blogImageUrl) {
        blogImageUrl = getConfiguredShareImage();
        console.warn('⚠️ No blog featured image found, using the admin share image');
      }
      
      console.log('🎯 Blog sharing - Featured image URL:', blogImageUrl);
      
      return {
        title: data.title,
        description: data.excerpt || data.content?.substring(0, 160) || 'Read this blog post by Carmen Lorraine Photography',
        image: blogImageUrl,
        url: `${baseUrl}/blog/${data.slug}`,
        hashtags: ['photography', 'blog', 'carmenlorraine', 'lifestyle']
      };
    
    case 'image':
      // Get the image URL - handle both string and object formats
      let imageUrl = '';
      if (data.url) {
        imageUrl = data.url;
      } else if (data.secure_url) {
        imageUrl = data.secure_url;
      } else if (typeof data === 'object' && data.url) {
        imageUrl = data.url;
      }
      
      // Ensure HTTPS URL for better compatibility
      if (imageUrl && imageUrl.startsWith('http://')) {
        imageUrl = imageUrl.replace('http://', 'https://');
      }
      
      console.log('🎯 Image sharing - Image URL:', imageUrl);
      
      return {
        title: `${data.alt || 'Beautiful Photo'} - Carmen Lorraine Photography`,
        description: `Check out this stunning photo from Carmen Lorraine Photography`,
        image: imageUrl,
        url: `${baseUrl}/image/${data.publicId}`,
        hashtags: ['photography', 'art', 'carmenlorraine', 'beautiful']
      };
    
    case 'package':
      // Get the package image - handle both string and object formats
      let packageImageUrl = '';
      
      // Try heroImage first (if available)
      if (data.heroImage) {
        if (typeof data.heroImage === 'object') {
          packageImageUrl = data.heroImage.url || data.heroImage.secure_url || '';
        } else if (typeof data.heroImage === 'string') {
          packageImageUrl = data.heroImage;
        }
      }
      // Fall back to image field
      else if (data.image) {
        if (typeof data.image === 'object') {
          packageImageUrl = data.image.url || data.image.secure_url || '';
        } else if (typeof data.image === 'string') {
          packageImageUrl = data.image;
        }
      }
      
      // Ensure HTTPS URL for better compatibility
      if (packageImageUrl && packageImageUrl.startsWith('http://')) {
        packageImageUrl = packageImageUrl.replace('http://', 'https://');
      }
      
      // Optimize package image for social sharing (1200x630 for Open Graph)
      if (packageImageUrl && packageImageUrl.includes('cloudinary.com')) {
        // Add social sharing transformation if not already optimized
        if (!packageImageUrl.includes('w_1200') && !packageImageUrl.includes('c_fill')) {
          const baseUrl = packageImageUrl.split('/upload/')[0];
          const publicId = packageImageUrl.split('/upload/')[1];
          packageImageUrl = `${baseUrl}/upload/w_1200,h_630,c_fill,q_auto,f_auto/${publicId}`;
        }
      }
      
      // Fallback to default logo if no package image
      if (!packageImageUrl) {
        // Try to get logo from localStorage first
        try {
          const logoData = localStorage.getItem('website_logo');
          if (logoData) {
            const logoParsed = JSON.parse(logoData);
            if (logoParsed.imageUrl && logoParsed.imageUrl.trim() !== '') {
              packageImageUrl = fitShareImageUrl(logoParsed.imageUrl.trim());
            }
          }
        } catch (e) {
          // Fallback to hardcoded logo if localStorage fails
        }
        
        // Final fallback to the share image saved in admin
        if (!packageImageUrl) {
          packageImageUrl = getConfiguredShareImage();
        }
        
        // Only log warning in development
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ No package image found, using default logo with social sharing dimensions');
        }
      }
      
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Package sharing - Image URL:', packageImageUrl);
      }
      
      // Build price information for sharing - use converted prices if provided, otherwise fall back to original data
      const displayPrice = options?.displayPrice ?? (data.isSpecial && data.specialPrice ? data.specialPrice : data.price);
      const displayCurrency = options?.displayCurrency || data.currency || 'R';
      const originalPrice = options?.originalPrice ?? data.price;
      
      // Format price with currency symbol using formatCurrency utility
      let priceInfo: string;
      if (displayPrice) {
        priceInfo = formatCurrency(displayPrice, displayCurrency);
      } else {
        priceInfo = 'Price on request';
      }
      
      // Check if there's a discount
      const hasDiscount = Boolean(
        data.isSpecial && 
        originalPrice && 
        displayPrice && 
        originalPrice > displayPrice
      );
      
      // Add original price info if there's a discount
      let originalPriceInfo = '';
      if (hasDiscount && originalPrice && originalPrice !== displayPrice) {
        originalPriceInfo = ` (was ${formatCurrency(originalPrice, displayCurrency)})`;
      }
      
      // Build engaging description with package details
      let packageDescription = '';
      
      // Start with a friendly introduction
      packageDescription = `📸 ${data.name}\n\n`;
      
      // Add short description or full description (truncated if too long)
      const baseDescription = data.short || data.description || `A beautiful ${data.category} photography package`;
      if (baseDescription.length > 200) {
        packageDescription += `${baseDescription.substring(0, 200)}...\n\n`;
      } else {
        packageDescription += `${baseDescription}\n\n`;
      }
      
      // Add package details
      packageDescription += `💰 Price: ${priceInfo}${originalPriceInfo}\n`;
      
      const durationText = formatPackageDuration(data.durationMinutes || data.duration);
      if (durationText) {
        packageDescription += `⏱️ Duration: ${durationText}\n`;
      }
      
      // Add category if available
      if (data.category) {
        packageDescription += `📁 Category: ${data.category}\n`;
      }
      
      packageDescription += `\n✨ Perfect for capturing your special moments!`;
      
      // Add cache-busting to the share URL
      const packageCacheBuster = data.updatedAt ? new Date(data.updatedAt).getTime() : Date.now();
      const packageShareUrl = `${baseUrl}/packages/${data.slug || data.id}?v=${packageCacheBuster}`;
      
      const finalShareContent = {
        title: `${data.name} - Carmen Lorraine Photography`,
        description: packageDescription,
        image: packageImageUrl,
        url: packageShareUrl,
        hashtags: ['photography', data.category?.toLowerCase(), 'carmenlorraine', 'photographypackage'],
        category: data.category
      };
      
      return finalShareContent;
    
    default:
      return {
        title: 'Carmen Lorraine Photography',
        description: 'Professional photography services in Cape Town',
        image: '',
        url: baseUrl,
        hashtags: ['photography', 'carmenlorraine']
      };
  }
};

// Services share helper
export const generateServiceShareContent = (service: any): ShareableContent => {
  const baseUrl = getPublicSiteUrl();
  const cacheBuster = service.updatedAt ? new Date(service.updatedAt).getTime() : Date.now();
  const url = `${baseUrl}/services/${service.slug}?v=${cacheBuster}`;
  return {
    title: `${service.name} - Carmen Lorraine Photography`,
    description: service.short || service.description || 'Professional photography services',
    image: service.image,
    url,
    hashtags: ['photography', 'services', 'capeTown'],
  } as ShareableContent;
};

// Generate sharing URLs for a specific platform
export const generateSharingUrl = (platform: SocialPlatform, content: ShareableContent): string => {
  const { title, description, image, url, hashtags, category } = content;
  const hashtagString = hashtags ? hashtags.map(tag => `#${tag}`).join(' ') : '';
  const shareTemplate = (typeof window !== 'undefined' && localStorage.getItem('share_message_template')) || '';
  const templateHasUrl = shareTemplate.toLowerCase().includes('{url}');
  // Build a professional default if no template provided
  const defaultTemplate = `${title}${category ? `\nCategory: ${category}` : ''}`;
  // Token replacement for {title}, {category}, {url}, {description}, {hashtags}
  const buildMessageFromTemplate = (tmpl: string) => {
    if (!tmpl) return defaultTemplate;
    return tmpl
      .replace(/\{title\}/gi, title)
      .replace(/\{category\}/gi, category || '')
      .replace(/\{url\}/gi, url)
      .replace(/\{description\}/gi, description)
      .replace(/\{hashtags\}/gi, hashtagString);
  };
  
  console.log(`🔗 Generating ${platform} share URL with:`, { title, description, image, url });
  
  switch (platform) {
    case 'whatsapp': {
      const whatsappMessage = shareTemplate ? buildMessageFromTemplate(shareTemplate) : title;
      const whatsappBody = templateHasUrl ? whatsappMessage : `${whatsappMessage}\n${url}`;
      return `https://wa.me/?text=${encodeURIComponent(whatsappBody)}`;
    }
    
    case 'twitter':
      // X (formerly Twitter) sharing - URL first, then text and tags
      // Truncate description if too long for Twitter (280 char limit, but we need space for URL and hashtags)
      const maxTwitterLength = 200;
      const twitterDescription = description.length > maxTwitterLength 
        ? description.substring(0, maxTwitterLength) + '...' 
        : description;
      const twitterBody = shareTemplate
        ? buildMessageFromTemplate(shareTemplate)
        : `${title}\n\n${twitterDescription}${hashtagString ? '\n\n' + hashtagString : ''}`;
      const twitterText = templateHasUrl ? twitterBody : `${url}\n\n${twitterBody}`;
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}`;
      console.log(`🐦 X URL:`, twitterUrl);
      console.log(`🐦 X Note: URL first, then text/tags. Image preview will be generated from the shared URL's Open Graph meta tags`);
      return twitterUrl;
    
    case 'facebook':
      return SHARING_URLS.facebook(url);
    
    case 'pinterest':
      // Pinterest supports direct image sharing - most reliable method
      const pinterestUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(image)}&description=${encodeURIComponent(`${title} - ${description}`)}`;
      console.log(`📌 Pinterest URL:`, pinterestUrl);
      console.log(`📌 Pinterest Note: Using direct image parameter - most reliable: ${image}`);
      return pinterestUrl;
    
    case 'linkedin':
      return SHARING_URLS.linkedin(url, title, title);
    
    case 'telegram':
      // Telegram - URL first, then text and tags
      const telegramText = shareTemplate
        ? buildMessageFromTemplate(shareTemplate)
        : `${title}\n\n${description}${hashtagString ? '\n\n' + hashtagString : ''}`;
      const telegramBody = templateHasUrl ? telegramText : `${url}\n\n${telegramText}`;
      const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(telegramBody)}`;
      console.log(`✈️ Telegram URL:`, telegramUrl);
      console.log(`✈️ Telegram Note: URL first, then text/tags. Image preview will be generated from the shared URL's Open Graph meta tags`);
      return telegramUrl;
    
    case 'instagram':
      // Instagram doesn't support direct URL sharing - use Web Share API or copy link
      // For now, return the URL so it can be copied or used with Web Share API
      // The ShareButton component will handle Web Share API when available
      console.log(`📷 Instagram: Use Web Share API or copy link. URL: ${url}`);
      return url;
    
    case 'email': {
      const emailBody = shareTemplate
        ? (templateHasUrl ? buildMessageFromTemplate(shareTemplate) : `${buildMessageFromTemplate(shareTemplate)}\n${url}`)
        : `${title}\n${url}`;
      return `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(emailBody)}`;
    }
    
    case 'reddit':
      const redditUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
      console.log(`🤖 Reddit URL:`, redditUrl);
      return redditUrl;
    
    case 'tumblr':
      // Tumblr sharing - URL first, then text and tags
      const tumblrCaption = shareTemplate ? url + '\n\n' + buildMessageFromTemplate(shareTemplate) : `${url}\n\n${description}`;
      const tumblrUrl = `https://www.tumblr.com/widgets/share/tool?posttype=link&title=${encodeURIComponent(title)}&caption=${encodeURIComponent(tumblrCaption)}&content=${encodeURIComponent(url)}`;
      console.log(`📝 Tumblr URL:`, tumblrUrl);
      return tumblrUrl;
    
    default:
      return url;
  }
};

